<template>
  <v-container fluid>
    <avs-card>
      <template v-slot:title>
        <div class="d-flex align-center" style="width:100%; gap:12px; flex-wrap:wrap;">
          <div>
            <div class="text-h6 font-weight-bold">
              <v-icon left color="primary">mdi-package-variant-closed</v-icon>
              {{ isPrincipal ? $t("Demandes de livraison reçues") : $t("Mes demandes de livraison") }}
            </div>
            <div class="text-caption grey--text">
              {{ totalCount }} {{ totalCount > 1 ? $t("demandes") : $t("demande") }}
              <span v-if="isPrincipal && pendingCount > 0">
                · <strong class="warning--text">{{ pendingCount }}</strong> {{ $t("en attente") }}
              </span>
              <span v-if="!isPrincipal && canProcess && internalPendingCount > 0">
                · <strong class="deep-orange--text">{{ internalPendingCount }}</strong>
                {{ $t("à valider en interne") }}
              </span>
            </div>
          </div>
          <v-spacer />
          <v-btn
            v-if="isPrincipal && canConfigure"
            color="grey darken-2"
            outlined
            @click="openConfig"
          >
            <v-icon left>mdi-cog-outline</v-icon>
            {{ $t("Configuration") }}
          </v-btn>
          <v-btn
            v-if="!isPrincipal && canCreate"
            color="primary"
            depressed
            :to="{ name: 'RefillRequestNew' }"
          >
            <v-icon left>mdi-plus</v-icon>
            {{ $t("Nouvelle demande") }}
          </v-btn>
        </div>
      </template>

      <template v-slot:content>
        <!-- Banniere info -->
        <v-alert
          v-if="showInfoBanner"
          type="info"
          text
          dense
          dismissible
          border="left"
          colored-border
          class="mx-4 mt-4 mb-0"
          @input="dismissInfoBanner"
        >
          <template v-if="isPrincipal">
            <strong>{{ $t("Espace de gestion des demandes reçues") }}</strong> —
            {{ $t("vous voyez ici toutes les demandes de livraison envoyées par les sociétés clientes. Validez-les (avec une note optionnelle) ou refusez-les. Le bouton Configuration permet de définir les emails à notifier à chaque nouvelle demande.") }}
          </template>
          <template v-else>
            <strong>{{ $t("À quoi sert ce menu ?") }}</strong>
            {{ $t("Vous pouvez ici demander une livraison de carburant à votre fournisseur principal et suivre le traitement de chaque demande. Joignez le bon de commande scanné (max 20 Mo) lors de la création.") }}
          </template>
        </v-alert>

        <!-- Filtres -->
        <div class="filters-row pa-4">
          <v-row dense>
            <v-col cols="6" sm="3" md="2">
              <v-select
                v-model="filters.status"
                :items="statusOptions"
                :label="$t('Statut')"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-flag-outline"
                @change="reloadList"
              />
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <v-autocomplete
                v-model="filters.product"
                :items="productOptions"
                item-text="name"
                item-value="id"
                :label="$t('Produit')"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-gas-station"
                @change="reloadList"
              />
            </v-col>
            <v-col v-if="isPrincipal" cols="12" sm="6" md="3">
              <v-autocomplete
                v-model="filters.requester_company"
                :items="companyOptions"
                item-text="name"
                item-value="pk"
                :label="$t('Société demandeuse')"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-domain"
                @change="reloadList"
              />
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <v-menu
                v-model="menuStartDate"
                :close-on-content-click="false"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="filters.start_date"
                    :label="$t('Date début')"
                    outlined
                    dense
                    hide-details
                    readonly
                    clearable
                    prepend-inner-icon="mdi-calendar-start"
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="onClearStartDate"
                  />
                </template>
                <v-date-picker
                  v-model="filters.start_date"
                  @input="menuStartDate = false; reloadList()"
                />
              </v-menu>
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <v-menu
                v-model="menuEndDate"
                :close-on-content-click="false"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="filters.end_date"
                    :label="$t('Date fin')"
                    outlined
                    dense
                    hide-details
                    readonly
                    clearable
                    prepend-inner-icon="mdi-calendar-end"
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="onClearEndDate"
                  />
                </template>
                <v-date-picker
                  v-model="filters.end_date"
                  @input="menuEndDate = false; reloadList()"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="1" class="d-flex align-center">
              <v-btn
                v-if="hasFilters"
                text
                small
                color="grey darken-1"
                @click="clearFilters"
              >
                <v-icon small left>mdi-close</v-icon>
                {{ $t("Effacer") }}
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <!-- Data table avec dynamique scrolling + tri serveur -->
        <v-data-table
          class="ma-4 bgTable"
          :headers="headers"
          :items="items"
          :loading="loading"
          :no-data-text="$t('Aucune demande pour le moment')"
          :loading-text="$t('Chargement...')"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          must-sort
          hide-default-footer
          disable-pagination
          item-key="id"
          @click:row="openDetail"
        >
          <template v-slot:item.company="{ item }">
            <div class="font-weight-medium">
              {{ isPrincipal ? item.requester_company_name : item.principal_company_name }}
            </div>
            <div class="text-caption grey--text">
              {{ $t('par') }} {{ item.created_by_name || '—' }}
            </div>
          </template>

          <template v-slot:item.product_name="{ item }">
            <span v-if="item.product_name">{{ item.product_name }}</span>
            <span v-else class="grey--text">—</span>
          </template>

          <template v-slot:item.quantity="{ item }">
            <span class="font-weight-bold">{{ formatNumber(item.quantity) }}</span> L
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.desired_delivery_date="{ item }">
            <span v-if="item.desired_delivery_date">
              {{ formatDateOnly(item.desired_delivery_date) }}
            </span>
            <span v-else class="grey--text">—</span>
          </template>

          <template v-slot:item.processed_at="{ item }">
            <div v-if="item.processed_at">
              {{ formatDate(item.processed_at) }}
              <div class="text-caption grey--text">
                {{ $t('par') }} {{ item.processed_by_name || '—' }}
              </div>
            </div>
            <span v-else class="grey--text">—</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip small :color="statusColor(item.status)" text-color="white">
              {{ item.status_label }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              :title="$t('Voir')"
              @click.stop="openDetail(item)"
            >
              <v-icon size="22">mdi-eye-outline</v-icon>
            </v-btn>
            <v-btn
              v-if="canInternalValidate(item)"
              icon
              color="deep-orange"
              :title="$t('Valider en interne et envoyer au principal')"
              @click.stop="askInternalValidate(item)"
            >
              <v-icon size="22">mdi-shield-check-outline</v-icon>
            </v-btn>
            <v-btn
              v-if="isPrincipal && canProcess && item.status === 'pending'"
              icon
              color="success"
              :title="$t('Traiter cette demande')"
              @click.stop="askProcess(item)"
            >
              <v-icon size="22">mdi-check-circle-outline</v-icon>
            </v-btn>
            <v-btn
              v-if="canCancel(item)"
              icon
              color="grey darken-1"
              :title="$t('Annuler ma demande')"
              @click.stop="askCancel(item)"
            >
              <v-icon size="22">mdi-cancel</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <!-- Loader bas / fin de liste -->
        <div ref="sentinel" class="scroll-sentinel">
          <div v-if="isFetchingMore" class="loading-more">
            <v-progress-circular indeterminate size="22" width="2" color="primary" />
            <span class="ml-2">{{ $t('Chargement…') }}</span>
          </div>
          <div v-else-if="isLastPage && items.length > 0" class="end-of-list">
            <v-icon small color="grey">mdi-check</v-icon>
            {{ $t('Toutes les demandes sont chargées') }}
          </div>
        </div>
      </template>
    </avs-card>

    <!-- ============= Dialog detail ============= -->
    <v-dialog v-model="detailDialog" max-width="640">
      <v-card v-if="selected">
        <v-card-title class="text-h6">
          {{ $t("Demande") }} #{{ selected.id }}
          <v-spacer />
          <v-chip small :color="statusColor(selected.status)" text-color="white">
            {{ selected.status_label }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <div class="detail-row">
            <span class="detail-label">{{ $t("Société demandeuse") }}</span>
            <span>{{ selected.requester_company_name }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ $t("Société principale") }}</span>
            <span>{{ selected.principal_company_name }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ $t("Produit") }}</span>
            <span>{{ selected.product_name || "—" }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ $t("Quantité") }}</span>
            <span class="font-weight-bold">{{ formatNumber(selected.quantity) }} L</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ $t("Date de demande") }}</span>
            <span>{{ formatDate(selected.created_at) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ $t("Livraison souhaitée") }}</span>
            <span>{{ formatDateOnly(selected.desired_delivery_date) || "—" }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ $t("Site de livraison") }}</span>
            <span>{{ selected.delivery_shop || "—" }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ $t("Destination") }}</span>
            <span>{{ selected.destination || "—" }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ $t("Demandé par") }}</span>
            <span>{{ selected.created_by_name || "—" }}</span>
          </div>
          <div v-if="selected.notes" class="detail-row">
            <span class="detail-label">{{ $t("Notes") }}</span>
            <span>{{ selected.notes }}</span>
          </div>
          <div v-if="selected.order_document_url" class="mt-3">
            <div class="detail-label mb-1">{{ $t("Bon de commande") }}</div>
            <v-btn
              :href="selected.order_document_url"
              target="_blank"
              rel="noopener"
              outlined
              small
            >
              <v-icon left small>mdi-file-document-outline</v-icon>
              {{ $t("Ouvrir le document") }}
            </v-btn>
          </div>
          <div v-if="selected.processed_by_name" class="mt-3 detail-block">
            <div class="detail-label">
              {{ selected.status === 'rejected' ? $t('Refusée par') : $t('Traitée par') }}
            </div>
            <div>
              {{ selected.processed_by_name }}
              {{ $t("le") }} {{ formatDate(selected.processed_at) }}
            </div>
            <div v-if="selected.processing_notes" class="text-caption grey--text mt-1">
              {{ selected.processing_notes }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="canInternalValidate(selected)"
            color="deep-orange"
            depressed
            dark
            @click="askInternalValidate(selected)"
          >
            <v-icon left>mdi-shield-check-outline</v-icon>
            {{ $t("Valider et envoyer au principal") }}
          </v-btn>
          <v-btn
            v-if="canProcessItem(selected)"
            color="success"
            depressed
            @click="askProcess(selected)"
          >
            <v-icon left>mdi-check</v-icon>
            {{ $t("Marquer validée") }}
          </v-btn>
          <v-btn
            v-if="canCancel(selected)"
            color="grey darken-1"
            outlined
            @click="askCancel(selected)"
          >
            <v-icon left>mdi-cancel</v-icon>
            {{ $t("Annuler ma demande") }}
          </v-btn>
          <v-spacer />
          <v-btn text @click="detailDialog = false">{{ $t("Fermer") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============= Dialog confirmation traitement ============= -->
    <v-dialog v-model="processDialog" max-width="560" persistent>
      <v-card v-if="selected" class="process-card">
        <!-- Header bandeau -->
        <div class="process-header">
          <v-icon size="44" color="white">mdi-clipboard-check-outline</v-icon>
          <div class="ml-3">
            <div class="process-header__overline">
              {{ $t("Traitement de la demande") }} #{{ selected.id }}
            </div>
            <div class="process-header__title">
              {{ $t("Que souhaitez-vous faire ?") }}
            </div>
          </div>
        </div>

        <v-card-text class="pt-5">
          <!-- Resume de la demande -->
          <div class="process-summary">
            <div class="process-summary__row">
              <v-icon small color="grey darken-1">mdi-domain</v-icon>
              <span class="label">{{ $t("Demandeur") }}</span>
              <span class="value">{{ selected.requester_company_name }}</span>
            </div>
            <div class="process-summary__row">
              <v-icon small color="grey darken-1">mdi-gas-station</v-icon>
              <span class="label">{{ $t("Produit") }}</span>
              <span class="value">{{ selected.product_name || '—' }}</span>
            </div>
            <div class="process-summary__row">
              <v-icon small color="grey darken-1">mdi-numeric</v-icon>
              <span class="label">{{ $t("Quantité") }}</span>
              <span class="value strong">{{ formatNumber(selected.quantity) }} L</span>
            </div>
            <div class="process-summary__row">
              <v-icon small color="grey darken-1">mdi-clock-outline</v-icon>
              <span class="label">{{ $t("Soumise le") }}</span>
              <span class="value">{{ formatDate(selected.created_at) }}</span>
            </div>
          </div>

          <!-- Note -->
          <div class="process-note-label mt-4 mb-1">
            <v-icon small color="grey darken-1">mdi-note-text-outline</v-icon>
            {{ $t("Note de traitement (optionnel)") }}
          </div>
          <v-textarea
            v-model="processNotes"
            :placeholder="$t('Ex : commande livrée le 12/05, ou raison du refus…')"
            outlined
            dense
            rows="3"
            auto-grow
            hide-details
          />

          <!-- Aide visuelle -->
          <div class="process-hint mt-3">
            <v-icon x-small color="info">mdi-information-outline</v-icon>
            {{ $t("La société demandeuse verra votre note et le statut final.") }}
          </div>
        </v-card-text>

        <v-divider />

        <!-- Actions : 2 zones distinctes -->
        <v-card-actions class="process-actions">
          <v-btn
            outlined
            color="error"
            :loading="acting && processMode === 'reject'"
            :disabled="acting && processMode !== 'reject'"
            @click="confirmReject"
          >
            <v-icon left>mdi-close-circle-outline</v-icon>
            {{ $t("Refuser") }}
          </v-btn>
          <v-spacer />
          <v-btn text :disabled="acting" @click="processDialog = false">
            {{ $t("Annuler") }}
          </v-btn>
          <v-btn
            color="success"
            depressed
            :loading="acting && processMode === 'process'"
            :disabled="acting && processMode !== 'process'"
            @click="confirmProcess"
          >
            <v-icon left>mdi-check-bold</v-icon>
            {{ $t("Marquer traitée") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============= Dialog config emails ============= -->
    <v-dialog v-model="configDialog" max-width="540">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="primary">mdi-email-multiple-outline</v-icon>
          {{ $t("Emails à notifier") }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">
            {{ $t("Ces adresses recevront un email à chaque nouvelle demande de livraison, avec le bon de commande en pièce jointe.") }}
          </p>
          <v-combobox
            v-model="configEmails"
            :label="$t('Adresses email')"
            :hint="$t('Tapez puis Entrée pour ajouter')"
            persistent-hint
            multiple
            chips
            deletable-chips
            outlined
            small-chips
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="configDialog = false">{{ $t("Annuler") }}</v-btn>
          <v-btn color="primary" depressed :loading="savingConfig" @click="saveConfig">
            {{ $t("Sauvegarder") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============= Dialog validation interne ============= -->
    <v-dialog v-model="internalDialog" max-width="560" persistent>
      <v-card v-if="selected" class="process-card">
        <div class="process-header" style="background: linear-gradient(135deg,#bf360c 0%,#e64a19 100%);">
          <v-icon size="44" color="white">mdi-shield-check-outline</v-icon>
          <div class="ml-3">
            <div class="process-header__overline">
              {{ $t("Validation interne") }} #{{ selected.id }}
            </div>
            <div class="process-header__title">
              {{ $t("Valider et envoyer au principal ?") }}
            </div>
          </div>
        </div>

        <v-card-text class="pt-5">
          <div class="process-summary">
            <div class="process-summary__row">
              <v-icon small color="grey darken-1">mdi-account-outline</v-icon>
              <span class="label">{{ $t("Demandeur") }}</span>
              <span class="value">{{ selected.created_by_name || '—' }}</span>
            </div>
            <div class="process-summary__row">
              <v-icon small color="grey darken-1">mdi-gas-station</v-icon>
              <span class="label">{{ $t("Produit") }}</span>
              <span class="value">{{ selected.product_name || '—' }}</span>
            </div>
            <div class="process-summary__row">
              <v-icon small color="grey darken-1">mdi-numeric</v-icon>
              <span class="label">{{ $t("Quantité") }}</span>
              <span class="value strong">{{ formatNumber(selected.quantity) }} L</span>
            </div>
            <div class="process-summary__row" v-if="selected.delivery_shop">
              <v-icon small color="grey darken-1">mdi-map-marker</v-icon>
              <span class="label">{{ $t("Site") }}</span>
              <span class="value">{{ selected.delivery_shop }}</span>
            </div>
          </div>

          <div class="process-note-label mt-4 mb-1">
            <v-icon small color="grey darken-1">mdi-note-text-outline</v-icon>
            {{ $t("Note (optionnel)") }}
          </div>
          <v-textarea
            v-model="internalNotes"
            :placeholder="$t('Ex : raison du refus, validation rapide, etc.')"
            outlined
            dense
            rows="3"
            auto-grow
            hide-details
          />

          <div class="process-hint mt-3">
            <v-icon x-small color="info">mdi-information-outline</v-icon>
            {{ $t("La validation enverra la demande au principal et déclenchera l'email.") }}
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="process-actions">
          <v-btn
            outlined
            color="error"
            :loading="internalActing && internalMode === 'reject'"
            :disabled="internalActing && internalMode !== 'reject'"
            @click="confirmInternalReject"
          >
            <v-icon left>mdi-close-circle-outline</v-icon>
            {{ $t("Refuser") }}
          </v-btn>
          <v-spacer />
          <v-btn text :disabled="internalActing" @click="internalDialog = false">
            {{ $t("Annuler") }}
          </v-btn>
          <v-btn
            color="deep-orange"
            depressed
            dark
            :loading="internalActing && internalMode === 'validate'"
            :disabled="internalActing && internalMode !== 'validate'"
            @click="confirmInternalValidate"
          >
            <v-icon left>mdi-shield-check-outline</v-icon>
            {{ $t("Valider et envoyer") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============= Dialog confirmation annulation ============= -->
    <v-dialog v-model="cancelDialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="grey darken-1">mdi-cancel</v-icon>
          {{ $t("Annuler cette demande ?") }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-0">
            {{ $t("La demande sera marquée comme annulée et ne pourra plus être traitée. Cette action est définitive.") }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="cancelling" @click="cancelDialog = false">
            {{ $t("Retour") }}
          </v-btn>
          <v-btn
            color="grey darken-1"
            depressed
            dark
            :loading="cancelling"
            @click="confirmCancel"
          >
            {{ $t("Confirmer l'annulation") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  RefillRequestService,
  ProductDetailService,
  CompanyService,
} from "@/services"
import { toDatetime } from "@/utils/datetime"

export default {
  name: "RefillRequestList",

  data() {
    return {
      // Liste + scroll
      loading: false,
      isFetchingMore: false,
      isLastPage: false,
      page: 1,
      items: [],
      totalCount: 0,

      // Filtres
      filters: {
        status: null,
        product: null,
        requester_company: null,
        start_date: null,
        end_date: null,
      },
      menuStartDate: false,
      menuEndDate: false,
      productOptions: [],
      companyOptions: [],
      statusOptionsAll: [
        { text: this.$t("En attente de validation interne"), value: "pending_internal" },
        { text: this.$t("En attente"), value: "pending" },
        { text: this.$t("Validée"), value: "processed" },
        { text: this.$t("Refusée"), value: "rejected" },
        { text: this.$t("Annulée"), value: "cancelled" },
      ],

      // Dialogs
      detailDialog: false,
      selected: null,
      processDialog: false,
      processMode: "process", // process | reject
      processNotes: "",
      acting: false,

      // Cancel
      cancelDialog: false,
      cancelTarget: null,
      cancelling: false,

      // Validation interne
      internalDialog: false,
      internalMode: "validate", // validate | reject
      internalNotes: "",
      internalActing: false,

      // Config emails
      configDialog: false,
      configEmails: [],
      savingConfig: false,

      // Banniere info
      showInfoBanner: !localStorage.getItem("refill_list_info_dismissed"),

      // Scroll observer
      scrollObserver: null,

      // Tri serveur
      sortBy: "created_at",
      sortDesc: true,
    }
  },

  watch: {
    sortBy() {
      this.reloadList()
    },
    sortDesc() {
      this.reloadList()
    },
  },

  computed: {
    isPrincipal() {
      return !!(this.$store.getters["app/getCompany"]?.is_principal)
    },
    pendingCount() {
      return this.items.filter(i => i.status === "pending").length
    },
    internalPendingCount() {
      return this.items.filter(i => i.status === "pending_internal").length
    },
    statusOptions() {
      // Le principal ne voit jamais les demandes en attente de validation interne,
      // donc on retire l'option du filtre de son cote.
      if (this.isPrincipal) {
        return this.statusOptionsAll.filter(o => o.value !== "pending_internal")
      }
      return this.statusOptionsAll
    },
    isAdminRole() {
      const company = this.$store.getters["app/getCompany"]
      const role = company && company.company_role
      return this.$store.getters["app/isCompanyOwner"]
        || role === "administrator"
        || role === "manager"
    },
    privilegeCodes() {
      const c = this.$store.getters["app/userPrivilegeCodes"]
      return c && c.has ? c : new Set()
    },
    canCreate() {
      return this.isAdminRole || this.privilegeCodes.has("edit_refill_request")
    },
    canProcess() {
      return this.isAdminRole || this.privilegeCodes.has("process_refill_request")
    },
    canConfigure() {
      return this.isAdminRole || this.privilegeCodes.has("config_refill_request")
    },
    hasFilters() {
      return !!(this.filters.status || this.filters.product
        || this.filters.requester_company
        || this.filters.start_date || this.filters.end_date)
    },
    headers() {
      // value = cle technique alignee avec le mapping `ordering` envoye au backend
      // (cf. methode buildOrderingParam)
      const cols = [
        {
          text: this.isPrincipal ? this.$t("Demandeur") : this.$t("Destinataire"),
          value: "company",
          sortable: true,
        },
        { text: this.$t("Produit"), value: "product_name", sortable: true },
        { text: this.$t("Quantité"), value: "quantity", align: "end", sortable: true },
        { text: this.$t("Date demande"), value: "created_at", sortable: true },
        { text: this.$t("Livraison souhaitée"), value: "desired_delivery_date", sortable: true },
        { text: this.$t("Validation / Refus"), value: "processed_at", sortable: true },
        { text: this.$t("Statut"), value: "status", align: "center", sortable: true },
        { text: this.$t("Actions"), value: "actions", sortable: false, align: "center" },
      ]
      return cols
    },
    currentUserId() {
      return this.$store.state.app.user?.user?.pk
        || this.$store.state.app.user?.user?.id
        || this.$store.state.app.user?.id
    },
  },

  created() {
    if (this.$store.state.app.userPrivileges === null) {
      this.$store.dispatch("app/loadUserPrivileges")
    }
    this.fetchProducts()
    if (this.isPrincipal) this.fetchCompanies()
    this.reloadList()
  },

  mounted() {
    this.setupScrollObserver()
  },

  beforeDestroy() {
    if (this.scrollObserver) this.scrollObserver.disconnect()
  },

  methods: {
    // ----- Format -----
    formatNumber(v) {
      const n = Number(v || 0)
      return n.toLocaleString("fr-FR")
    },
    formatDate(v) {
      if (!v) return ""
      try { return toDatetime(v) } catch (e) { return new Date(v).toLocaleString("fr-FR") }
    },
    formatDateOnly(v) {
      if (!v) return ""
      // YYYY-MM-DD -> DD/MM/YYYY (date locale, sans fuseau)
      const [y, m, d] = String(v).split("-")
      if (y && m && d) return `${d}/${m}/${y}`
      return v
    },
    statusColor(s) {
      if (s === "pending_internal") return "deep-orange"
      if (s === "pending") return "warning"
      if (s === "processed") return "success"
      if (s === "rejected") return "error"
      if (s === "cancelled") return "grey darken-1"
      return "grey"
    },

    canCancel(item) {
      // Le createur (ou admin de la societe demandeuse) peut annuler une demande
      // tant qu'elle n'est pas encore traitee (interne ou principal).
      if (!item) return false
      if (this.isPrincipal) return false
      if (!["pending", "pending_internal"].includes(item.status)) return false
      return item.created_by === this.currentUserId || this.isAdminRole
    },

    canInternalValidate(item) {
      // Cote demandeur : admin/operateur avec privilege peut valider interne
      if (!item || this.isPrincipal) return false
      if (item.status !== "pending_internal") return false
      return this.canProcess
    },

    // ----- Banniere info -----
    dismissInfoBanner() {
      try {
        localStorage.setItem("refill_list_info_dismissed", "1")
      } catch (e) {
        // localStorage indisponible (mode prive) - ignorer
      }
      this.showInfoBanner = false
    },

    // ----- Filtres -----
    onClearStartDate() {
      this.filters.start_date = null
      this.reloadList()
    },
    onClearEndDate() {
      this.filters.end_date = null
      this.reloadList()
    },
    clearFilters() {
      this.filters = {
        status: null, product: null, requester_company: null,
        start_date: null, end_date: null,
      }
      this.reloadList()
    },

    // ----- Listes auxiliaires -----
    fetchProducts() {
      ProductDetailService.getprincipalAll()
        .then(r => { this.productOptions = r.results || r || [] })
        .catch(() => { this.productOptions = [] })
    },
    fetchCompanies() {
      // Pour le principal : recuperer toutes les societes (cf. TransactionDetail)
      CompanyService.getAllCompanies()
        .then(r => {
          this.companyOptions = (r || []).filter(c => !c.is_principal)
        })
        .catch(() => { this.companyOptions = [] })
    },

    // ----- Liste paginee + infinite scroll -----
    buildOrderingParam() {
      // Mapping cle de colonne -> champ backend
      const map = {
        company: this.isPrincipal ? "requester_company__name" : "principal_company__name",
        product_name: "product__name",
        quantity: "quantity",
        created_at: "created_at",
        processed_at: "processed_at",
        status: "status",
      }
      const field = map[this.sortBy] || "created_at"
      return this.sortDesc ? `-${field}` : field
    },

    buildParams(page) {
      const p = { page, page_size: 20, ordering: this.buildOrderingParam() }
      if (this.filters.status) p.status = this.filters.status
      if (this.filters.product) p.product = this.filters.product
      if (this.filters.requester_company) p.requester_company = this.filters.requester_company
      if (this.filters.start_date) p.start_date = this.filters.start_date
      if (this.filters.end_date) p.end_date = this.filters.end_date
      return p
    },

    reloadList() {
      this.loading = true
      this.page = 1
      this.isLastPage = false
      this.items = []
      RefillRequestService.list(this.buildParams(1))
        .then(r => {
          const results = Array.isArray(r) ? r : (r?.results || [])
          this.items = results
          this.totalCount = (r && r.count != null) ? r.count : results.length
          this.isLastPage = !r || !r.next || results.length === 0
          this.page = 2
        })
        .catch(() => {
          this.$store.commit("app/message", {
            msg: this.$t("Impossible de charger les demandes"),
            color: "error",
          })
        })
        .finally(() => { this.loading = false })
    },

    async fetchMore() {
      if (this.loading || this.isFetchingMore || this.isLastPage) return
      this.isFetchingMore = true
      try {
        const r = await RefillRequestService.list(this.buildParams(this.page))
        const results = Array.isArray(r) ? r : (r?.results || [])
        if (results.length === 0) {
          this.isLastPage = true
        } else {
          this.items.push(...results)
          this.page += 1
          if (!r || !r.next) this.isLastPage = true
        }
      } catch (e) {
        if (e?.response?.status === 404) this.isLastPage = true
      } finally {
        this.isFetchingMore = false
      }
    },

    setupScrollObserver() {
      // IntersectionObserver sur la sentinelle bas-de-liste
      if (!("IntersectionObserver" in window) || !this.$refs.sentinel) return
      this.scrollObserver = new IntersectionObserver(entries => {
        entries.forEach(e => { if (e.isIntersecting) this.fetchMore() })
      }, { rootMargin: "200px" })
      this.scrollObserver.observe(this.$refs.sentinel)
    },

    // ----- Actions -----
    openDetail(item) {
      this.selected = item
      this.detailDialog = true
    },

    canProcessItem(item) {
      return this.isPrincipal && this.canProcess && item && item.status === "pending"
    },

    askProcess(item) {
      this.selected = item
      this.processNotes = ""
      this.processMode = "process"
      this.processDialog = true
    },

    askInternalValidate(item) {
      this.selected = item
      this.internalNotes = ""
      this.internalMode = "validate"
      this.internalDialog = true
    },

    confirmInternalValidate() {
      if (!this.selected) return
      this.internalMode = "validate"
      this.internalActing = true
      RefillRequestService.internalValidate(this.selected.id, this.internalNotes || "")
        .then(updated => {
          this.afterAction(updated)
          this.internalDialog = false
          this.$store.commit("app/message", {
            msg: this.$t("Demande validée et envoyée au principal"),
            color: "success",
          })
        })
        .catch(err => {
          const msg = err?.response?.data?.detail || this.$t("Échec de la validation")
          this.$store.commit("app/message", { msg, color: "error" })
        })
        .finally(() => { this.internalActing = false })
    },

    confirmInternalReject() {
      if (!this.selected) return
      this.internalMode = "reject"
      this.internalActing = true
      RefillRequestService.internalReject(this.selected.id, this.internalNotes || "")
        .then(updated => {
          this.afterAction(updated)
          this.internalDialog = false
          this.$store.commit("app/message", {
            msg: this.$t("Demande refusée"),
            color: "info",
          })
        })
        .catch(err => {
          const msg = err?.response?.data?.detail || this.$t("Échec du refus")
          this.$store.commit("app/message", { msg, color: "error" })
        })
        .finally(() => { this.internalActing = false })
    },

    askCancel(item) {
      this.cancelTarget = item
      this.cancelDialog = true
    },

    confirmCancel() {
      if (!this.cancelTarget) return
      this.cancelling = true
      RefillRequestService.cancel(this.cancelTarget.id)
        .then(updated => {
          const idx = this.items.findIndex(i => i.id === updated.id)
          if (idx >= 0) this.$set(this.items, idx, updated)
          this.cancelDialog = false
          this.cancelTarget = null
          this.$store.commit("app/message", {
            msg: this.$t("Demande annulée"), color: "info",
          })
        })
        .catch(err => {
          const msg = err?.response?.data?.detail || this.$t("Échec de l'annulation")
          this.$store.commit("app/message", { msg, color: "error" })
        })
        .finally(() => { this.cancelling = false })
    },

    confirmProcess() {
      if (!this.selected) return
      this.processMode = "process"
      this.acting = true
      RefillRequestService.process(this.selected.id, this.processNotes || "")
        .then(updated => this.afterAction(updated))
        .catch(() => this.actionError())
        .finally(() => { this.acting = false })
    },

    confirmReject() {
      if (!this.selected) return
      this.processMode = "reject"
      this.acting = true
      RefillRequestService.reject(this.selected.id, this.processNotes || "")
        .then(updated => this.afterAction(updated))
        .catch(() => this.actionError())
        .finally(() => { this.acting = false })
    },

    afterAction(updated) {
      const idx = this.items.findIndex(i => i.id === updated.id)
      if (idx >= 0) this.$set(this.items, idx, updated)
      this.processDialog = false
      this.detailDialog = false
      this.selected = null
    },

    actionError() {
      this.$store.commit("app/message", {
        msg: this.$t("Échec de l'opération"), color: "error",
      })
    },

    // ----- Config emails -----
    openConfig() {
      this.configDialog = true
      RefillRequestService.getNotifConfig()
        .then(r => { this.configEmails = r.emails || [] })
        .catch(() => { this.configEmails = [] })
    },

    saveConfig() {
      this.savingConfig = true
      const cleaned = (this.configEmails || [])
        .map(e => (e || "").trim())
        .filter(Boolean)
      RefillRequestService.updateNotifConfig(cleaned)
        .then(() => {
          this.configDialog = false
          this.$store.commit("app/message", {
            msg: this.$t("Configuration enregistrée"), color: "success",
          })
        })
        .catch(err => {
          const msg = err?.response?.data?.emails?.[0]
            || err?.response?.data?.detail
            || this.$t("Échec de la sauvegarde")
          this.$store.commit("app/message", { msg, color: "error" })
        })
        .finally(() => { this.savingConfig = false })
    },
  },
}
</script>

<style scoped>
.filters-row {
  background: #f9fafb;
  border-bottom: 1px solid #eceff1;
}
.scroll-sentinel {
  min-height: 40px;
}
.loading-more,
.end-of-list {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #8892a4;
  font-size: 13px;
}

/* ===== Modal traitement ===== */
.process-card {
  border-radius: 12px;
  overflow: hidden;
}
.process-header {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #1c2437 0%, #2d3a52 100%);
  color: white;
  padding: 18px 24px;
}
.process-header__overline {
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  opacity: 0.7;
  margin-bottom: 2px;
}
.process-header__title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}

.process-summary {
  background: #f7f9fc;
  border: 1px solid #eceff1;
  border-radius: 10px;
  padding: 14px 16px;
}
.process-summary__row {
  display: grid;
  grid-template-columns: 22px 110px 1fr;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}
.process-summary__row + .process-summary__row {
  border-top: 1px solid #eceff1;
}
.process-summary .label {
  font-size: 12px;
  color: #757575;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.process-summary .value {
  font-size: 14px;
  color: #1c2437;
  text-align: right;
}
.process-summary .value.strong {
  font-weight: 700;
  color: #1976d2;
  font-size: 16px;
}

.process-note-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #424242;
}
.process-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #5f6368;
  background: #e3f2fd;
  padding: 8px 10px;
  border-radius: 6px;
}

.process-actions {
  padding: 12px 20px;
  background: #fafafa;
  gap: 6px;
}

@media (max-width: 600px) {
  .process-summary__row {
    grid-template-columns: 22px 1fr;
  }
  .process-summary .value {
    grid-column: 1 / -1;
    text-align: left;
    padding-left: 30px;
  }
}

/* ===== Modal detail ===== */
.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.detail-row:last-child { border-bottom: none; }
.detail-label {
  font-size: 12px;
  color: #757575;
  font-weight: 600;
  text-transform: uppercase;
}
.detail-block {
  background: #f5f7fa;
  padding: 12px;
  border-radius: 6px;
}
</style>
